import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { Button } from './button';
import { Badge } from './badge';
import { AlertCircle, Upload, X, ImageIcon, RefreshCw, CheckCircle2, AlertTriangle, FileWarning } from 'lucide-react';
import { uploadWithRetry, classifyError, UploadError } from '../../utils/uploadRetry';
import { compressImage, isCompressibleImageType, getCompressionSettings } from '../../utils/imageCompression';
import { ImageFormatHelper } from './image-format-helper';

// Define proper type that doesn't rely on schema recognition
type StorageId = Id<"_storage">;
type FileId = Id<"files">;

export interface ImageUploaderProps {
  projectId?: Id<"projects">;
  componentId?: Id<"pageComponents">;
  purpose: string;
  altText?: string;
  className?: string;
  onUploadComplete?: (fileId: FileId | null, url: string) => void;
  onUploadError?: (error: string) => void;
  maxSizeMB?: number;
  acceptedTypes?: string[];
  existingImageUrl?: string;
  existingFileId?: string;
  enableCompression?: boolean;
  compressionThresholdKB?: number;
  showHelper?: boolean;
}

export const ImageUploader = ({
  projectId,
  componentId,
  purpose,
  altText = "",
  className = "",
  onUploadComplete,
  onUploadError,
  maxSizeMB = 2,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'],
  existingImageUrl,
  existingFileId,
  enableCompression = true,
  compressionThresholdKB = 200,
  showHelper = false,
}: ImageUploaderProps) => {
  // State for the file selection and upload process
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(existingImageUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadStage, setUploadStage] = useState<'idle' | 'compressing' | 'uploading' | 'processing' | 'complete'>('idle');
  const [retryCount, setRetryCount] = useState(0);
  const [detailedError, setDetailedError] = useState<UploadError | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use string paths instead of direct API references
  const generateUploadUrl = useMutation('filesFunctions:generateUploadUrlFn' as any);
  const saveFileMetadata = useMutation('filesFunctions:saveFileMetadataFn' as any);
  const deleteFile = useMutation('filesFunctions:deleteFileFn' as any);
  
  // State to track storage ID for getting URL
  const [storageId, setStorageId] = useState<Id<"_storage"> | null>(null);
  
  // Query to get file URL from storage ID
  const urlData = useQuery('filesFunctions:getUrlFromStorageIdFn' as any, 
    storageId ? { storageId } : "skip");

  // Reset the success state after a delay
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (uploadSuccess) {
      timer = setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [uploadSuccess]);

  // Handle URL data response
  useEffect(() => {
    if (urlData && urlData.url && storageId) {
      console.log('Got Convex URL from query:', urlData.url);
      setPreview(urlData.url);
      
      // Check for pending upload and trigger callback
      const pendingUpload = (window as any).__pendingUpload;
      if (pendingUpload && pendingUpload.onUploadComplete) {
        console.log('Upload complete, notifying parent with Convex URL');
        pendingUpload.onUploadComplete(pendingUpload.fileId, urlData.url);
        
        // Clear the pending upload
        delete (window as any).__pendingUpload;
      }
      
      // Clear the storage ID to avoid re-triggering
      setStorageId(null);
    }
  }, [urlData, storageId]);

  // Update preview when selected file changes (for temporary preview during upload)
  useEffect(() => {
    if (!selectedFile) return;

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    // Clean up the object URL when component unmounts or selected file changes
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  // Smooth progress animation
  const animateProgress = (from: number, to: number, duration: number = 500) => {
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const current = from + (to - from) * progress;
      setUploadProgress(Math.round(current));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  };

  // Check image dimensions
  const checkImageDimensions = (file: File): Promise<{ width: number; height: number } | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
  };

  // Enhanced drag and drop with validation
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragged items are valid
    const items = Array.from(e.dataTransfer.items);
    const hasInvalidType = items.some(item => 
      !acceptedTypes.includes(item.type)
    );
    
    setIsDragging(!hasInvalidType);
  }, [acceptedTypes]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      validateAndSetFile(droppedFiles[0]);
    }
  }, []);

  // Enhanced file validation with helpful messages
  const validateAndSetFile = async (file: File) => {
    setError(null);
    setDetailedError(null);
    setCompressionInfo(null);

    // File type validation with format suggestions
    if (!acceptedTypes.includes(file.type)) {
      const supportedFormats = acceptedTypes
        .map(type => type.split('/')[1].toUpperCase())
        .join(', ');
      setError(`Please use ${supportedFormats} format. Your file is ${file.type.split('/')[1].toUpperCase()}.`);
      return;
    }

    // File size validation with helpful context
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    
    if (file.size > maxSizeBytes) {
      setError(`Your image is ${fileSizeMB}MB. Please use an image under ${maxSizeMB}MB.`);
      
      // Suggest compression
      if (file.size > maxSizeBytes * 2) {
        setError(`Your image is ${fileSizeMB}MB (too large). Try reducing image dimensions or quality before uploading.`);
      }
      return;
    }

    // Image dimension check for specific purposes
    const dimensions = await checkImageDimensions(file);
    if (dimensions) {
      if (purpose === 'hero' && dimensions.width < 1920) {
        setError(`Hero images should be at least 1920px wide. Your image is ${dimensions.width}px wide.`);
        return;
      }
    }

    setSelectedFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Enhanced upload with retry and compression
  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setUploadStage('compressing');
      setError(null);
      setDetailedError(null);
      setRetryCount(0);
      setCompressionInfo(null);
      
      // Animate to 20%
      animateProgress(0, 20);

      // Compress if needed
      let fileToUpload = selectedFile;
      if (enableCompression && isCompressibleImageType(selectedFile.type)) {
        const fileSizeKB = selectedFile.size / 1024;
        
        if (fileSizeKB > compressionThresholdKB) {
          setIsCompressing(true);
          
          try {
            // Get compression settings based on purpose
            const compressionSettings = getCompressionSettings(purpose);
            
            // Compress the image
            const compressedFile = await compressImage(selectedFile, compressionSettings);
            
            // Show compression info
            const originalSizeMB = selectedFile.size / 1024 / 1024;
            const compressedSizeMB = compressedFile.size / 1024 / 1024;
            const saved = ((selectedFile.size - compressedFile.size) / selectedFile.size * 100).toFixed(1);
            
            if (compressedFile.size < selectedFile.size) {
              setCompressionInfo(
                `Optimized from ${originalSizeMB.toFixed(2)}MB to ${compressedSizeMB.toFixed(2)}MB (${saved}% smaller)`
              );
            }
            
            fileToUpload = compressedFile;
          } catch (error) {
            console.error('Compression failed, using original file:', error);
            // Continue with original file if compression fails
          } finally {
            setIsCompressing(false);
          }
        }
      }

      // Upload with retry
      setUploadStage('uploading');
      animateProgress(20, 40);

      const uploadResult = await uploadWithRetry(
        async () => {
          // Generate upload URL
          const uploadUrl = await generateUploadUrl({
            contentType: fileToUpload.type,
            maxSize: fileToUpload.size,
          });
          
          if (!uploadUrl) {
            throw new Error('Failed to generate upload URL');
          }
          
          animateProgress(40, 60);

          // Upload file
          const response = await fetch(uploadUrl, {
            method: "POST",
            headers: { "Content-Type": fileToUpload.type },
            body: fileToUpload,
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Upload failed: ${response.status} - ${errorText}`);
          }
          
          return response.json();
        },
        {
          maxRetries: 3,
          onRetry: (attempt, error) => {
            setRetryCount(attempt);
            console.log(`Retry attempt ${attempt}:`, error);
          }
        }
      );

      // Process metadata
      setUploadStage('processing');
      animateProgress(60, 90);

      const saveResult = await saveFileMetadata({
        storageId: uploadResult.storageId,
        filename: selectedFile.name,
        contentType: selectedFile.type,
        size: selectedFile.size,
        projectId,
        componentId,
        purpose,
        altText,
      });

      // Complete
      setUploadStage('complete');
      animateProgress(90, 100);
      setUploadSuccess(true);
      
      // Set the storage ID to trigger the URL query
      setStorageId(uploadResult.storageId);
      
      // Store the file ID for when the URL is ready
      const uploadedFileId = saveResult.fileId;
      
      // Reset after success
      setTimeout(() => {
        setSelectedFile(null);
        setUploadStage('idle');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);
      
      // The actual callback will be triggered in the useEffect when urlData is received
      // For now, store the file ID to use later
      (window as any).__pendingUpload = {
        fileId: uploadedFileId,
        onUploadComplete
      };
      
    } catch (err) {
      const uploadError = classifyError(err);
      setDetailedError(uploadError);
      setError(uploadError.message);
      
      if (onUploadError) {
        onUploadError(uploadError.message);
      }
    } finally {
      setIsUploading(false);
      setIsCompressing(false);
    }
  };

  // Retry handler
  const handleRetry = () => {
    setRetryCount(0);
    setDetailedError(null);
    handleUpload();
  };

  // Handle removing the selected file or existing image
  const handleRemove = async () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    setDetailedError(null);
    setCompressionInfo(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // If there's an existing file that's been saved, delete it from storage
    if (existingFileId) {
      try {
        // Convert the string ID to a proper Convex ID using casting for deleteFile
        // This is a temporary workaround - in a production app, you'd ensure proper typing
        await deleteFile({ 
          fileId: existingFileId as unknown as Id<"files"> 
        });
        
        if (onUploadComplete) {
          onUploadComplete(null, '');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete file';
        setError(errorMessage);
        console.error('Error deleting file:', err);
        
        if (onUploadError) {
          onUploadError(errorMessage);
        }
      }
    }
  };

  // Determine purpose for helper
  const helperPurpose = purpose.includes('hero') || purpose.includes('background') ? 'hero' : 
                       purpose.includes('gallery') ? 'gallery' : 'general';

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Helper section */}
      {showHelper && (
        <ImageFormatHelper purpose={helperPurpose} />
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={acceptedTypes.join(',')}
      />
      
      {/* Enhanced drag and drop area / Preview */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer 
          transition-all duration-200 
          ${isDragging ? 'border-primary bg-primary/5 scale-[1.02]' : 'border-gray-300 hover:border-primary'}
          ${preview ? 'h-64' : 'h-40'}
          ${error ? 'border-red-300 bg-red-50/50' : ''}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        {preview ? (
          <div className="relative h-full w-full">
            <img
              src={preview}
              alt={altText || "Image preview"}
              className="h-full max-h-56 mx-auto object-contain rounded"
            />
            
            {/* Action buttons overlay */}
            <div className="absolute top-0 right-0 p-1">
              <Button 
                variant="destructive" 
                size="icon" 
                className="h-8 w-8 rounded-full" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <ImageIcon className={`h-12 w-12 ${error ? 'text-red-400' : 'text-gray-400'}`} />
            <div className="font-medium text-gray-700">
              <p>Drag & drop an image here</p>
              <p className="text-sm">or click to browse</p>
            </div>
            <div className="flex gap-2 mt-2">
              <Badge variant="outline" className="text-xs">
                Max {maxSizeMB}MB
              </Badge>
              <Badge variant="outline" className="text-xs">
                {acceptedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}
              </Badge>
            </div>
          </div>
        )}
        
        {/* Visual feedback overlay */}
        {isDragging && (
          <div className="absolute inset-0 border-2 border-primary rounded-lg pointer-events-none">
            <div className="absolute inset-0 bg-primary/10 animate-pulse" />
          </div>
        )}
      </div>
      
      {/* Compression indicator */}
      {isCompressing && (
        <div className="flex items-center gap-2 text-primary text-sm">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Optimizing image for better performance...</span>
        </div>
      )}
      
      {/* Compression info */}
      {compressionInfo && (
        <div className="flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle2 className="h-4 w-4" />
          <span>{compressionInfo}</span>
        </div>
      )}
      
      {/* Enhanced error display with retry */}
      {detailedError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{detailedError.message}</p>
              {detailedError.recoverable && retryCount < 3 && (
                <button
                  onClick={handleRetry}
                  className="text-sm text-red-600 hover:text-red-700 underline mt-1"
                >
                  Try again ({3 - retryCount} attempts remaining)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Simple error message fallback */}
      {error && !detailedError && (
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
      
      {/* Upload progress with stage indicator */}
      {isUploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="capitalize">{uploadStage.replace('_', ' ')}...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-gradient-to-r from-primary to-primary/80 h-full rounded-full transition-all duration-300 relative"
              style={{ width: `${uploadProgress}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse" />
            </div>
          </div>
          {retryCount > 0 && (
            <p className="text-xs text-gray-500">Retry attempt {retryCount}/3</p>
          )}
        </div>
      )}
      
      {/* Button controls */}
      <div className="flex gap-2">
        {selectedFile && !isUploading && (
          <Button
            variant="default"
            className="flex items-center gap-2"
            onClick={handleUpload}
            disabled={isUploading}
          >
            <Upload className="h-4 w-4" />
            Upload Image
          </Button>
        )}
        
        {isUploading && (
          <Button variant="outline" disabled className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            {uploadStage.charAt(0).toUpperCase() + uploadStage.slice(1)}... {uploadProgress}%
          </Button>
        )}
        
        {uploadSuccess && (
          <Button variant="outline" className="flex items-center gap-2 text-green-600" disabled>
            <CheckCircle2 className="h-4 w-4" />
            Upload Successful
          </Button>
        )}
      </div>
    </div>
  );
};